import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  tap
} from 'rxjs/operators';
import {
  getCurrentDealVersionDistinct,
  selectBenchmarkCurveState,
  selectCalendarWeekOf
} from '../state/reducer';
import { PermissionsFacade } from './permissions.facade';
import { PreferencesFacade } from './preferences.facade';
import {
  BenchmarkCurveMapping,
  curveMappings,
  isCurveAlwaysAccessible,
  shouldLoad
} from '../helpers/benchmark-curve';
import {
  BenchmarkCurve,
  BenchmarkProvider,
  MappedBenchmarkCurve
} from '../types/benchmark.types';
import {
  BenchmarkCurveIdentifier,
  BenchmarkMMDCurve
} from '../types/user-preferences.types';
import { BenchmarkCurveData } from '../state/benchmark-curves/state';
import {
  LoadCurves,
  LoadWeeklyCurves,
  SelectCurve
} from '../state/benchmark-curves/actions';
import { getFinalDateForDeal } from '../helpers/issue/deal-status';

export interface WeeklyBenchmarkCurveFacadeState {
  loading: boolean;
  curves: BenchmarkCurve[];
}

export interface BenchmarkCurveFacadeState {
  loading: boolean;
  curve: MappedBenchmarkCurve | undefined;
  selectedCurve?: BenchmarkMMDCurve[];
}

export function mapCurve({
  curveState,
  date,
  curveMapping
}: {
  curveState: BenchmarkCurveData;
  date: string;
  curveMapping: BenchmarkCurveMapping;
}): BenchmarkCurveFacadeState {
  if (
    curveState.loaded &&
    curveState.date === date &&
    curveState.provider === curveMapping.provider
  ) {
    const curve = curveState.data.find(
      (c) => c.identifier === curveMapping.identifier
    );

    return {
      loading: false,
      curve: {
        ...curve,
        identifier: curveMapping.identifier,
        provider: curveState.provider,
        shortName: curveMapping.shortName,
        typeId: curveMapping.typeId
      }
    };
  } else {
    return {
      loading: curveState.loading,
      curve: undefined
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class BenchmarkCurvesFacade {
  public readonly weeklyCurveState$: Observable<WeeklyBenchmarkCurveFacadeState>;
  public readonly singleCurveState$: Observable<BenchmarkCurveFacadeState>;

  private lastStableCurve = BenchmarkCurveIdentifier.MacInterpolated;

  constructor(
    private store: Store<Record<string, unknown>>,
    private permissionsFacade: PermissionsFacade,
    private preferencesFacade: PreferencesFacade
  ) {
    this.weeklyCurveState$ = combineLatest(
      store.pipe(
        select(selectBenchmarkCurveState),
        map(({ weekly }) => weekly),
        distinctUntilChanged<BenchmarkCurveData>(isEqual)
      ),
      store.pipe(
        select(selectCalendarWeekOf),
        filter((weekOf) => Boolean(weekOf)),
        distinctUntilChanged<string>(isEqual)
      ),
      this.permissionsFacade.getPermission('mmd'),
      this.preferencesFacade.getPreference('defaultBenchmarkCurve')
    ).pipe(
      filter(([curveState, _date, hasMMD, _preferredCurve]) =>
        this.updateMMDPermissionIfNeeded(hasMMD, curveState)
      ),
      map(([curveState, date, hasMMD, preferredCurve]) => {
        let curve = preferredCurve;
        if (curve.startsWith('MMD') && !hasMMD) {
          curve = BenchmarkCurveIdentifier.MacInterpolated;
        }

        const curveMapping = curveMappings.find(
          (mapping) => mapping.identifier === curve
        );

        return { curveState, date, curveMapping };
      }),
      tap(({ curveState, date, curveMapping }) =>
        this.ensureWeeklyCurveLoaded(curveState, date, curveMapping.provider)
      ),
      map(({ curveState, date, curveMapping }) => {
        if (
          curveState.loaded &&
          curveState.date === date &&
          curveState.provider === curveMapping.provider
        ) {
          return {
            loading: false,
            curves: curveState.data.filter(
              (curve) => curve.identifier === curveMapping.identifier
            )
          };
        } else {
          return {
            loading: true,
            curves: []
          };
        }
      }),
      distinctUntilChanged(isEqual),
      publishReplay(1),
      refCount()
    );

    this.singleCurveState$ = combineLatest(
      store.pipe(
        select(selectBenchmarkCurveState),
        map(({ single, selectedCurve }) => ({
          curveState: single,
          selectedCurve
        })),
        distinctUntilChanged<{
          curveState: BenchmarkCurveData;
          selectedCurve: BenchmarkCurveIdentifier;
        }>(isEqual)
      ),
      store.pipe(
        getCurrentDealVersionDistinct,
        filter((deal) => Boolean(deal)),
        map(getFinalDateForDeal),
        distinctUntilChanged(isEqual)
      ),
      this.permissionsFacade.getPermission('mmd'),
      this.preferencesFacade.getPreference('defaultBenchmarkCurve')
    ).pipe(
      filter(([{ curveState }, _date, hasMMD, _preferredCurve]) =>
        this.updateMMDPermissionIfNeeded(hasMMD, curveState)
      ),
      map(([{ curveState, selectedCurve }, date, hasMMD, preferredCurve]) => {
        let curve = selectedCurve || preferredCurve;
        if (curve.startsWith('MMD') && !hasMMD) {
          curve = this.lastStableCurve;
        } else if (isCurveAlwaysAccessible(curve)) {
          this.lastStableCurve = curve;
        }

        const curveMapping = curveMappings.find(
          (mapping) => mapping.identifier === curve
        );

        return {
          curveState,
          selectedCurve,
          date,
          curveMapping
        };
      }),
      filter(({ curveState, selectedCurve, curveMapping }) =>
        this.ensureSelection(curveState, selectedCurve, curveMapping.identifier)
      ),
      tap(({ curveState, date, curveMapping }) =>
        this.ensureCurveLoaded(curveState, date, curveMapping.provider)
      ),
      map(mapCurve),
      distinctUntilChanged(isEqual),
      publishReplay(1),
      refCount()
    );
  }

  public selectCurve(curve: BenchmarkCurveIdentifier) {
    setTimeout(() => {
      this.store.dispatch(new SelectCurve(curve));
    }, 1);
  }

  private updateMMDPermissionIfNeeded(
    hasMMD: boolean,
    curveState: BenchmarkCurveData
  ): boolean {
    if (
      hasMMD &&
      curveState.errorCode === 403 &&
      curveState.provider === BenchmarkProvider.mmd
    ) {
      setTimeout(() => this.permissionsFacade.setMmdAccess(false), 1);
      return false;
    } else {
      return true;
    }
  }

  private ensureSelection(
    curveState: BenchmarkCurveData,
    currentCurve: BenchmarkCurveIdentifier,
    desiredCurve: BenchmarkCurveIdentifier
  ) {
    if (curveState.loaded && currentCurve !== desiredCurve) {
      this.selectCurve(desiredCurve);
      return false;
    } else {
      return true;
    }
  }

  private ensureWeeklyCurveLoaded(
    state: BenchmarkCurveData,
    date: string,
    provider: BenchmarkProvider
  ) {
    if (shouldLoad(state, date, provider)) {
      setTimeout(() => {
        this.store.dispatch(new LoadWeeklyCurves({ date, provider }));
      }, 1);
    }
  }

  private ensureCurveLoaded(
    state: BenchmarkCurveData,
    date: string,
    provider: BenchmarkProvider
  ) {
    if (shouldLoad(state, date, provider)) {
      setTimeout(() => {
        this.store.dispatch(new LoadCurves({ date, provider }));
      }, 1);
    }
  }
}
